import React, { useEffect, useState } from "react";
import { RotateCcw, Loader2, Trash2, LifeBuoy } from "lucide-react";
import api from "../api";

function RecoverInvoices({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const run = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data: preview } = await api.get("/admin/recovery/manual-invoices/preview");
      if (!preview.missing_count) {
        setMsg("All manual invoices are present. Nothing to rebuild.");
        return;
      }
      const list = preview.missing.map((m) => `  · ${m.invoice_number} — ${m.customer || "?"} — $${Number(m.total || 0).toFixed(2)} — ${m.status}`).join("\n");
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Rebuild ${preview.missing_count} missing manual invoice(s) from their Orders records?\n\n${list}`)) return;
      const { data } = await api.post("/admin/recovery/manual-invoices/rebuild");
      setMsg(`Rebuilt ${data.rebuilt_count} invoice(s): ${data.rebuilt.join(", ")}`);
      onDone && onDone();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Recovery failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4">
      <button
        data-testid="recover-missing-invoices-btn"
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-2 px-4 py-2 border border-[#3DDC97]/50 text-[#3DDC97] hover:bg-[#3DDC97]/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
      >
        {busy ? <Loader2 className="animate-spin" size={12} /> : <LifeBuoy size={12} />}
        Recover Missing Manual Invoices
      </button>
      {msg && <div data-testid="recover-missing-invoices-msg" className="mt-2 text-xs text-[#8A94A6] font-mono-tech">{msg}</div>}
    </div>
  );
}

function BinRow({ g, onRestore, busy }) {
  return (
    <div data-testid={`recycle-bin-row-${g.group_id}`} className="bg-[#0C0C12] p-4 flex flex-wrap items-center gap-4">
      <div className="font-mono-tech text-xs text-[#00C3FF] w-24">{g.label}</div>
      <div className="flex-1 min-w-[160px] text-sm text-white truncate">{g.customer || "—"}</div>
      <div className="font-mono-tech text-xs text-white w-20 text-right">${Number(g.total || 0).toFixed(2)}</div>
      <div className="font-mono-tech text-[10px] text-[#5C657A] uppercase tracking-wider w-56">
        {[...new Set(g.statuses)].join("/")} · {g.reason} · {g.deleted_at ? new Date(g.deleted_at).toLocaleString() : ""}
      </div>
      <button
        data-testid={`recycle-bin-restore-${g.group_id}`}
        onClick={() => onRestore(g)}
        disabled={busy}
        className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00C3FF]/50 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.18em] disabled:opacity-40"
      >
        {busy ? <Loader2 className="animate-spin" size={12} /> : <RotateCcw size={12} />} Restore
      </button>
    </div>
  );
}

export default function RecycleBin() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/recycle-bin").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const restore = async (g) => {
    setBusyId(g.group_id);
    try {
      const { data } = await api.post(`/admin/recycle-bin/${g.group_id}/restore`);
      setMsg(`Restored ${data.label}: ${data.restored.length} record(s)${data.already_present.length ? `, ${data.already_present.length} already present` : ""}.`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Restore failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="recycle-bin-panel" className="mt-10 pt-8 border-t border-white/10">
      <div className="overline mb-2 flex items-center gap-2"><Trash2 size={12} /> RECYCLE BIN · {items.length}</div>
      <p className="text-sm text-[#8A94A6] mb-4">Every deleted order / invoice is kept here and can be restored.</p>
      <RecoverInvoices onDone={load} />
      {msg && <div data-testid="recycle-bin-msg" className="mb-3 text-xs text-[#8A94A6] font-mono-tech">{msg}</div>}
      <div className="space-y-px bg-white/5">
        {items.length === 0 ? (
          <div data-testid="recycle-bin-empty" className="bg-[#0C0C12] p-6 text-center text-[#5C657A] font-mono-tech text-xs">Recycle bin is empty.</div>
        ) : items.map((g) => (
          <BinRow key={g.group_id} g={g} onRestore={restore} busy={busyId === g.group_id} />
        ))}
      </div>
    </div>
  );
}
